import $ from 'jquery';
import { Controller } from 'stimulus';
import { fileMatchesAccept } from '../util';

export default class extends Controller {
  static targets = [
    'color',
    'font',
    'dropzone',
    'dropzonePrompt',
    'preview',
    'previewImage',
    'logoInput',
    'removeLogo',
    'logoError',
  ];

  static values = {
    defaultLogoUrl: String,
    hasSavedLogo: Boolean,
    maxFileSize: Number,
  };

  connect() {
    this.submitting = false;
    this.captureTheme();
    this.initColorPickers();
    this.initFontPicker();
  }

  disconnect() {
    // Preview edits are applied as inline CSS variables on <html> (and the header
    // logo), which Turbo keeps across navigations. Roll them back when leaving the
    // page unless the form was saved, so unsaved previews don't leak app-wide.
    if (!this.submitting) {
      this.restoreTheme();
    }
  }

  captureTheme() {
    const root = document.documentElement.style;
    this.originalTheme = [
      '--primary-color',
      '--secondary-color',
      '--header-color',
      '--font',
    ].reduce((acc, prop) => ({ ...acc, [prop]: root.getPropertyValue(prop) }), {});
    this.originalLogos = this.headerLogos().map((logo) => ({
      logo,
      src: logo.getAttribute('src'),
      custom: logo.classList.contains('header__logo-image--custom'),
    }));
  }

  restoreTheme() {
    const root = document.documentElement.style;
    Object.entries(this.originalTheme).forEach(([prop, value]) => {
      if (value) {
        root.setProperty(prop, value);
      } else {
        root.removeProperty(prop);
      }
    });
    this.originalLogos.forEach(({ logo, src, custom }) => {
      logo.setAttribute('src', src);
      logo.classList.toggle('header__logo-image--custom', custom);
    });
  }

  onSubmit() {
    this.submitting = true;
  }

  onSubmitEnd(event) {
    // A failed save keeps the card mounted, so allow restore on later navigation.
    this.submitting = event.detail?.success ?? true;
  }

  swapPreviewLogo({ detail: { newFrame } }) {
    // Colors and font inherit :root live; only the unsaved logo needs swapping in.
    const previewLogo = newFrame.querySelector('.client-portal-login-preview .header__logo-image');
    if (!previewLogo) {
      return;
    }
    const logoSrc = this.currentLogoSrc();
    const isDefaultLogo = logoSrc === this.defaultLogoUrlValue;
    previewLogo.setAttribute('src', logoSrc);
    previewLogo.classList.toggle('header__logo-image--custom', !isDefaultLogo);
  }

  currentLogoSrc() {
    if (this.previewTarget.classList.contains('hidden')) {
      return this.defaultLogoUrlValue;
    }
    const src = this.previewImageTarget.getAttribute('src');
    return src?.length ? src : this.defaultLogoUrlValue;
  }

  initColorPickers() {
    this.colorTargets.forEach((input) => {
      input.addEventListener('input', () => {
        this.applyColor(input, input.value);
        this.markDirty();
      });
    });
  }

  initFontPicker() {
    if (typeof $ !== 'undefined' && $.fn && $.fn.selectize) {
      const controller = this;
      // Guard against selectize firing onChange while it initializes the
      // pre-selected option, which would mark the form dirty on page load.
      this.fontReady = false;
      $(this.fontTarget).selectize({
        render: {
          option(item) {
            return `<div class="block w-full" style="font-family:'${item.text}'">${item.text}</div>`;
          },
        },
        onChange() {
          if (!controller.fontReady) {
            return;
          }
          controller.fontChanged();
        },
      });
      this.fontReady = true;
    } else {
      this.fontTarget.addEventListener('change', () => this.fontChanged());
    }
  }

  fontChanged() {
    const name = this.fontTarget.selectedOptions[0].text;
    document.documentElement.style.setProperty('--font', name);
    this.markDirty();
  }

  reset() {
    this.colorTargets.forEach((input) => {
      const value = input.dataset.default;
      input.value = value;
      this.applyColor(input, value);
    });
    this.resetFont();
    this.markDirty();
  }

  resetFont() {
    const value = this.fontTarget.dataset.default;
    if (!value) {
      return;
    }
    if (this.fontTarget.selectize) {
      this.fontTarget.selectize.setValue(value, true); // silent: no onChange
    } else {
      this.fontTarget.value = value;
    }
    const name = this.fontTarget.selectedOptions[0].text;
    document.documentElement.style.setProperty('--font', name);
  }

  browse(event) {
    // Stop the click bubbling to the dropzone's own click->browse action, which
    // would otherwise open the file dialog twice (e.g. from the Replace control).
    if (event) {
      event.stopPropagation();
    }
    this.logoInputTarget.click();
  }

  dropzoneKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.browse();
    }
  }

  dragover(event) {
    event.preventDefault();
  }

  drop(event) {
    event.preventDefault();
    if (!event.dataTransfer.files.length) {
      return;
    }
    // Assign via DataTransfer rather than the read-only drop FileList for
    // consistent cross-browser support of programmatic input.files.
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(event.dataTransfer.files[0]);
    this.logoInputTarget.files = dataTransfer.files;
    this.logoSelected();
  }

  logoSelected() {
    const file = this.logoInputTarget.files[0];
    if (!file) {
      return;
    }
    if (!this.isValidLogo(file)) {
      this.showLogoError();
      this.logoInputTarget.value = '';
      return;
    }
    this.clearLogoError();
    this.removeLogoTarget.value = '0';
    const reader = new FileReader();
    reader.onload = (event) => {
      this.previewImageTarget.src = event.target.result;
      this.dropzonePromptTarget.classList.add('hidden');
      this.previewTarget.classList.remove('hidden');
      this.applyHeaderLogo(event.target.result);
    };
    reader.readAsDataURL(file);
    this.markDirty();
  }

  isValidLogo(file) {
    return (
      fileMatchesAccept(file, this.logoInputTarget.accept) && file.size <= this.maxFileSizeValue
    );
  }

  showLogoError() {
    this.logoErrorTarget.classList.remove('hidden');
  }

  clearLogoError() {
    this.logoErrorTarget.classList.add('hidden');
  }

  removeLogo(event) {
    if (event) {
      event.stopPropagation();
    }
    this.logoInputTarget.value = '';
    this.removeLogoTarget.value = this.hasSavedLogoValue ? '1' : '0';
    this.previewTarget.classList.add('hidden');
    this.dropzonePromptTarget.classList.remove('hidden');
    this.resetHeaderLogo();
    this.clearLogoError();
    this.markDirty();
  }

  headerLogos() {
    return [...document.querySelectorAll('[data-client-portal-brand-logo]')];
  }

  applyHeaderLogo(url) {
    this.headerLogos().forEach((logo) => {
      logo.src = url;
      logo.classList.add('header__logo-image--custom');
    });
  }

  resetHeaderLogo() {
    this.headerLogos().forEach((logo) => {
      logo.src = this.defaultLogoUrlValue;
      logo.classList.remove('header__logo-image--custom');
    });
  }

  markDirty() {
    this.dispatch('changed');
  }

  applyColor(input, value) {
    const name = input.dataset.colorName;
    document.documentElement.style.setProperty(`--${name}-color`, value);
  }
}
